import { useCallback, useEffect } from "react";
import { Check, CheckCheck, MessageSquare, Search, UserPlus } from "lucide-react";
import { PushBanner } from "./Push";
import { Link } from "react-router-dom";
import { api } from "../../connect/api";
import { useConnect } from "../../connect/store";
import { WfhCheckIn } from "./WfhCheckIn";
import { UpcomingDays } from "./UpcomingDays";
import { ago, Avatar, Badge, CEO_ORG, CEO_TITLE, Empty, Header, LoadMore, roleLine, usePaged } from "./ui";

export const ChatsList = () => {
  const { me } = useConnect();
  const fetcher = useCallback((p) => api.get("/conversations", { params: { page: p, limit: 30 } }).then((r) => r.data), []);
  const list = usePaged(fetcher, []);

  const reload = list.reload;
  useEffect(() => {
    const t = setInterval(reload, 10000);
    return () => clearInterval(t);
  }, [reload]);

  return (
    <div className="h-full flex flex-col">
      <Header title="Chats" sub={me.is_ceo ? `${CEO_TITLE} · ${CEO_ORG}` : `${me.department} · ${me.office}`} right={<><Link to="/connect/search" className="p-2 rounded-full hover:bg-white/10" data-testid="chats-search-button" aria-label="Search"><Search size={20} /></Link><Link to="/connect/directory" className="p-2 rounded-full hover:bg-white/10" data-testid="chats-new-button" aria-label="Find colleague"><UserPlus size={20} /></Link></>} />
      <div className="flex-1 overflow-y-auto" onScroll={list.onScroll} data-testid="chats-list">
        <PushBanner />
        <WfhCheckIn />
        <UpcomingDays />
        {!list.loading && list.items.length === 0 && <Empty icon={MessageSquare} title="No conversations yet" text={me?.directory_hidden ? "Colleagues can message you here; the Directory opens once your probation is confirmed." : "Open the Directory to message a colleague in your department."} />}
        <ul className="divide-y divide-white/5">
          {list.items.map((c) => (
            <li key={c.id}>
              <Link to={`/connect/chat/${c.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-white/[0.03] active:bg-white/[0.06] transition-colors" data-testid={`chat-item-${c.id}`}>
                <Avatar name={c.other.name} src={c.other.avatar} size={46} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <div className={`truncate ${c.unread ? "font-semibold text-white" : "font-medium text-slate-200"}`}>{c.other.name}</div>
                    <div className="text-[11px] text-slate-500 shrink-0">{ago(c.last_at)}</div>
                  </div>
                  <div className="text-xs text-slate-500 truncate">{roleLine(c.other)}</div>
                  <div className={`text-sm truncate mt-0.5 flex items-center gap-1 ${c.unread ? "text-slate-100" : "text-slate-400"}`}>{c.last_sender_id === me.id && (c.last_seen_at ? <CheckCheck size={14} className="text-blue-400 shrink-0" data-testid="receipt-seen" /> : <Check size={14} className="text-slate-500 shrink-0" data-testid="receipt-delivered" />)}<span className="truncate">{c.last_text || "Say hello"}</span></div>
                </div>
                <Badge n={c.unread} />
              </Link>
            </li>
          ))}
        </ul>
        <LoadMore hasMore={list.hasMore} loading={list.loading && list.items.length > 0} onClick={list.loadMore} />
      </div>
    </div>
  );
};
